import { supabase } from '../supabase';

// Playbook (034) — playbook_formations. No author-name resolution is
// needed anywhere in this service (formation cards show name/format/
// count, never "who created this"), so listing is a plain RLS-gated
// table select — no list RPC needed, unlike Schedule/Board which
// resolve profiles for author display. Every WRITE still goes through a
// SECURITY DEFINER RPC, never a raw insert/update/delete —
// playbook_formations has no INSERT/UPDATE/DELETE RLS policy at all,
// matching team_events'/team_board_posts' own precedent.

/** Every Formation/Front/Unit for a team within one book (unit_type),
 * newest first. RLS restricts this to team staff / Program leadership /
 * platform admin — a connected guardian gets zero rows back, by design
 * (see migration 034's own comments). */
export async function listPlaybookFormations(teamId, unitType) {
  const { data, error } = await supabase
    .from('playbook_formations')
    .select('*')
    .eq('team_id', teamId)
    .eq('unit_type', unitType)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

/** Management authority only (Team Admin/Head Coach/Assistant Coach, or
 * Program Owner/Admin, or platform admin — Assistant Coach is
 * deliberately included here, unlike Schedule/Board, per 034's own
 * documented product decision). Rejected server-side once season_status
 * is closeout/ended. unit_type is set only at creation and can never
 * change afterward. */
export async function createPlaybookFormation({ teamId, unitType, name, playersPerSide, footballType, document }) {
  const { data, error } = await supabase.rpc('create_playbook_formation', {
    p_team_id: teamId,
    p_unit_type: unitType,
    p_name: name,
    p_players_per_side: playersPerSide,
    p_football_type: footballType || null,
    p_document: document,
  });
  if (error) throw error;
  return data;
}

/** Same authority/lifecycle rules as createPlaybookFormation(). Full
 * editable-field replace (name/players_per_side/football_type/document
 * together) — team_id/unit_type/created_by/created_at can never change
 * through this call. */
export async function updatePlaybookFormation({ formationId, name, playersPerSide, footballType, document }) {
  const { data, error } = await supabase.rpc('update_playbook_formation', {
    p_formation_id: formationId,
    p_name: name,
    p_players_per_side: playersPerSide,
    p_football_type: footballType || null,
    p_document: document,
  });
  if (error) throw error;
  return data;
}

/** Same authority/lifecycle rules — permanent, no soft delete/restore
 * in Phase A. */
export async function deletePlaybookFormation(formationId) {
  const { error } = await supabase.rpc('delete_playbook_formation', { p_formation_id: formationId });
  if (error) throw error;
}

// Playbook Phase B.0 (035) — playbook_plays. Same "no list RPC, plain
// RLS-gated select" reasoning as Formations above (Play cards show
// name/format/source snapshots, never "who created this"). Every WRITE
// still goes through a SECURITY DEFINER RPC — playbook_plays has no
// INSERT/UPDATE/DELETE RLS policy at all, matching playbook_formations'
// own precedent exactly.

/** Every Play for a team within one book (unit_type), newest first.
 * Same RLS visibility as listPlaybookFormations() — a connected
 * guardian gets zero rows back, by design (see migration 035's own
 * comments). */
export async function listPlaybookPlays(teamId, unitType) {
  const { data, error } = await supabase
    .from('playbook_plays')
    .select('*')
    .eq('team_id', teamId)
    .eq('unit_type', unitType)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

/** Same authority/lifecycle rules as createPlaybookFormation(). Both
 * source_formation_id and opponent_source_formation_id are optional
 * provenance references (validated server-side to belong to this team,
 * and — for source_formation_id only — to match unitType) — a Play may
 * be built entirely from starter templates with neither set. */
export async function createPlaybookPlay({
  teamId, unitType, name, playersPerSide, footballType, sourceFormationId, opponentSourceFormationId, document,
}) {
  const { data, error } = await supabase.rpc('create_playbook_play', {
    p_team_id: teamId,
    p_unit_type: unitType,
    p_name: name,
    p_players_per_side: playersPerSide,
    p_football_type: footballType || null,
    p_source_formation_id: sourceFormationId || null,
    p_opponent_source_formation_id: opponentSourceFormationId || null,
    p_document: document,
  });
  if (error) throw error;
  return data;
}

/** Same authority/lifecycle rules. Full editable-field replace (name/
 * players_per_side/football_type/document together) — team_id/
 * unit_type/created_by/created_at/source_formation_id/
 * opponent_source_formation_id can never change through this call (a
 * Play's provenance links are fixed at creation, same as a Formation's
 * unit_type). */
export async function updatePlaybookPlay({ playId, name, playersPerSide, footballType, document }) {
  const { data, error } = await supabase.rpc('update_playbook_play', {
    p_play_id: playId,
    p_name: name,
    p_players_per_side: playersPerSide,
    p_football_type: footballType || null,
    p_document: document,
  });
  if (error) throw error;
  return data;
}

/** Same authority/lifecycle rules — permanent, no soft delete/restore.
 * Deletes only the Play row itself; never touches its source
 * Formation(s) (enforced server-side — see migration 035). */
export async function deletePlaybookPlay(playId) {
  const { error } = await supabase.rpc('delete_playbook_play', { p_play_id: playId });
  if (error) throw error;
}
